/**
 * Přílety (owner, 29 Oct 2026): "we need to know when the customers are really
 * coming in comparison to what they write in their reservations".
 *
 * Everyone who comes back today and tomorrow, sorted by when they are really at
 * the car: the airport's landing time (real, else expected) + 45 min for
 * Terminal 2 (Schengen) or + 75 min for Terminal 1 (non-Schengen). The flight
 * comes from the number the customer wrote; without one the row keeps the time
 * they wrote and says "bez letu". The airport board is read by the server every
 * 5 minutes — this page only shows that copy.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ArrivalRow } from '@smile/shared';
import { ApiError, api } from '../api/client';

type Day = 'dnes' | 'zitra' | 'vse';

function errText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

/** "2026-10-30 17:30:00" → "17:30" */
const hm = (w: string | null | undefined): string => (w ? w.slice(11, 16) : '—');
const dayOf = (w: string | null | undefined): string => (w ? w.slice(0, 10) : '');

function minutesText(m: number): string {
  const a = Math.abs(m);
  if (a < 60) return `${a} min`;
  const h = Math.floor(a / 60);
  const r = a % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/**
 * The verdict is about the FLIGHT against the time the customer wrote (they are told
 * to write their landing time); "u auta" is shown on its own. Only the 4-hour
 * tolerance is measured at the car.
 */
function Verdict({ r }: { r: ArrivalRow }) {
  const f = r.flightDiffMinutes ?? 0;
  const d = r.diffMinutes ?? 0;
  switch (r.verdict) {
    case 'ontime':
      return <span className="pl-v ok" title="Let přistává do 15 min od zapsaného času">let včas</span>;
    case 'later':
      return <span className="pl-v late">let o {minutesText(f)} později</span>;
    case 'earlier':
      return <span className="pl-v early">let o {minutesText(f)} dřív</span>;
    case 'beyond':
      return <span className="pl-v bad">u auta o {minutesText(d)} později · přes 4 h toleranci</span>;
    case 'cancelled':
      return <span className="pl-v bad">let zrušen</span>;
    case 'notfound':
      return <span className="pl-v none" title="Číslo letu nesedí na žádný přílet do Prahy kolem zapsaného času">let nenalezen</span>;
    default:
      return <span className="pl-v none">bez letu</span>;
  }
}

const KIND: Record<string, string> = { real: 'přistál', expected: 'očekáván', planned: 'plán' };

function Row({ r, today }: { r: ArrivalRow; today: string }) {
  const at = r.atCar ?? r.written;
  const tomorrow = dayOf(at) !== today;
  return (
    <tr className={`pl-${r.verdict}${r.pickedUp ? ' done' : ''}`}>
      <td className="pl-at">
        <b>{hm(at)}</b>
        {tomorrow && <span className="pl-day">{dayOf(at) > today ? 'zítra' : 'včera'}</span>}
        <div className="sub">{r.atCar ? `přistání + ${r.walkMinutes} min` : 'zapsaný čas'}</div>
      </td>
      <td>
        <Verdict r={r} />
        {r.pickedUp && <div className="sub">vyzvednuto</div>}
      </td>
      <td className="pl-written">
        {hm(r.written)}
        {dayOf(r.written) !== today && <span className="pl-day">{dayOf(r.written) > today ? 'zítra' : 'včera'}</span>}
      </td>
      <td>
        {r.flight ? (
          <>
            <b>{r.flight.number}</b> {r.flight.terminal && <span className={`pl-term ${r.flight.terminal}`}>{r.flight.terminal}</span>}
            <div className="sub">{r.flight.origin}</div>
          </>
        ) : (
          <span className="sub">{r.flightRaw || '—'}</span>
        )}
      </td>
      <td>
        {r.flight ? (
          <>
            {hm(r.flight.landing)} <span className="sub">{r.flight.landingKind ? KIND[r.flight.landingKind] : ''}</span>
            <div className="sub">plán {hm(r.flight.scheduled)} · {r.flight.stateText || '—'}</div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td>
        <b>{r.driverName || '—'}</b>
        <div className="sub">{r.driverPhone ? <a href={`tel:${r.driverPhone.replace(/[^\d+]/g, '')}`}>{r.driverPhone}</a> : '—'}</div>
      </td>
      <td>
        {r.spz ? <span className="spz">{r.spz}</span> : '—'}
        <div className="sub">{r.car || ''}</div>
      </td>
      <td>
        <Link to={`/rezervace/${r.reservationId}`}>{r.reservationId}</Link>
      </td>
    </tr>
  );
}

export function Prilety() {
  const q = useQuery({ queryKey: ['arrivals'], queryFn: () => api.arrivals(), refetchInterval: 60_000 });
  const [day, setDay] = useState<Day>('dnes');
  const [hideDone, setHideDone] = useState(true);
  const [onlyFlights, setOnlyFlights] = useState(false);
  const today = q.data ? q.data.from.slice(0, 10) : '';

  const rows = useMemo(() => {
    const all = q.data?.rows ?? [];
    return all.filter((r) => {
      const d = dayOf(r.written);
      if (day === 'dnes' && d !== today) return false;
      if (day === 'zitra' && d === today) return false;
      if (hideDone && r.pickedUp) return false;
      if (onlyFlights && !r.flight) return false;
      return true;
    });
  }, [q.data, day, hideDone, onlyFlights, today]);

  const counts = useMemo(() => {
    const c = { flights: 0, later: 0, beyond: 0, earlier: 0, none: 0 };
    for (const r of rows) {
      if (r.flight) c.flights++;
      if (r.verdict === 'later') c.later++;
      if (r.verdict === 'beyond' || r.verdict === 'cancelled') c.beyond++;
      if (r.verdict === 'earlier') c.earlier++;
      if (r.verdict === 'noflight' || r.verdict === 'notfound') c.none++;
    }
    return c;
  }, [rows]);

  const b = q.data?.board;
  const rules = q.data?.rules;
  return (
    <div className="container">
      <div className="pagehead">
        <h1>Přílety</h1>
        <span className="sub">
          {rules ? `u auta = přistání + ${rules.t2Minutes} min (T2, Schengen) nebo + ${rules.t1Minutes} min (T1, mimo Schengen)` : '…'}
          {b ? ` · letiště načteno ${b.fetchedAt ? hm(b.fetchedAt) : 'zatím ne'}` : ''}
        </span>
        <div className="acts pl-filters">
          {(['dnes', 'zitra', 'vse'] as Day[]).map((d) => (
            <button key={d} type="button" className={`btn sm${day === d ? ' primary' : ''}`} onClick={() => setDay(d)}>
              {d === 'dnes' ? 'Dnes' : d === 'zitra' ? 'Zítra' : 'Dnes i zítra'}
            </button>
          ))}
          <label className="pl-check">
            <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} /> skrýt vyzvednuté
          </label>
          <label className="pl-check">
            <input type="checkbox" checked={onlyFlights} onChange={(e) => setOnlyFlights(e.target.checked)} /> jen s letem
          </label>
        </div>
      </div>
      {b?.error && <div className="card state pl-warn">Letiště teď neodpovídá ({b.error}) — zobrazeno poslední načtení.</div>}
      {q.isError ? (
        <div className="card state">Nelze načíst: {errText(q.error)}</div>
      ) : q.isPending ? (
        <div className="card state">Načítám…</div>
      ) : (
        <section className="card">
          <div className="pl-summary sub">
            {rows.length} {rows.length === 1 ? 'návrat' : 'návratů'} · s letem {counts.flights} · později {counts.later} · dřív {counts.earlier} ·
            mimo toleranci / zrušeno {counts.beyond} · bez letu {counts.none}
          </div>
          {rows.length === 0 ? (
            <div className="empty">Nikdo.</div>
          ) : (
            <table className="qtable pl-table">
              <thead>
                <tr>
                  <th>U auta</th>
                  <th>Let oproti zapsanému</th>
                  <th>Zapsáno</th>
                  <th>Let</th>
                  <th>Přistání</th>
                  <th>Klient</th>
                  <th>Auto</th>
                  <th>Rezervace</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row key={r.reservationId} r={r} today={today} />
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
